import { Alert, Avatar, Divider, Grid, Stack, useMediaQuery } from "@mui/material";
import ChoresCard from "./ChoresCard";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "lib/firebase";
import { avatars } from "./ChoresView";
import AddEditChoreDialog from "./AddEditChoreDialog";
import { useRef, useState } from "react"
import DeleteConfirmationDialog from "./DeleteConfirmationDialog"
import Snackbar from "@mui/material/Snackbar"
import { isChoreComplete } from "./utils"
import ConfettiTrigger from "../animation/ConfettiTrigger"
import AddOtherTasksDialog from "./AddOtherTasksDialog"
import { getUserPoints } from "lib/purchaseUtils"
import { getFirebaseUidFromName } from "../../../utils/userMapping"
import { useSnackbar } from 'notistack';
import { updateUserPointsWithLevelUpDetection } from "../../../utils/levelUpNotifications";

// Simple function to get Firebase UID from assignee name
const getUserIdFromAssignee = (assigneeName) => {
  const uid = getFirebaseUidFromName(assigneeName)
  return uid
}

const AssigneeChoresSection = ({ chores, assignee, label }) => {
  const { enqueueSnackbar } = useSnackbar()
  const filteredChores = chores?.filter(
    (chore) => chore.category?.toLowerCase() === label?.toLowerCase()
  )
  const [openDialog, setOpenDialog] = useState(false)
  const [openTaskDialog, setOpenTaskDialog] = useState(false)
  const [newChore, setNewChore] = useState({
    id: null,
    description: "",
    assignee: "",
    frequency: "daily",
    category: label.toLowerCase(),
  })
  const [newTask, setNewTask] = useState({
    id: null,
    description: "",
    assignee: "",
    frequency: "daily",
    category: label.toLowerCase(),
  })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [choreToDelete, setChoreToDelete] = useState(null)
  const [open, setOpen] = useState(false)

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"))

  const confettiRefs = useRef([])

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setOpen(false)
  }

  const onCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
  }

  const onMarkDone = async (choreId) => {
    if (choreId === null) {
      return
    }
    try {
      const choreRef = doc(db, "chores", choreId)
      
      // Get the chore data to find the assignee
      const choreDoc = await getDoc(choreRef)
      if (!choreDoc.exists()) {
        return
      }
      
      const choreData = choreDoc.data()
      const assigneeName = choreData.assignee
      const chorePoints = choreData.points || 1
      

      // Update the chore completion timestamp
      await updateDoc(choreRef, {
        last: new Date(),
      })

      // Award points to the individual user who was assigned the chore
      if (assigneeName) {
        const userId = getUserIdFromAssignee(assigneeName)
        
        if (userId) {
          try {
            // Get current user points
            const currentPoints = await getUserPoints(userId)
            
            // Add the chore points to their total
            const newTotal = currentPoints + chorePoints
            
            // Use level-up detection function instead of direct updateUserPoints
            await updateUserPointsWithLevelUpDetection(userId, newTotal, enqueueSnackbar, assigneeName)
            
            // Show success snackbar with points and XP info
            enqueueSnackbar(
              `🎉 ${assigneeName} earned ${chorePoints} point${chorePoints !== 1 ? 's' : ''} and ${chorePoints} XP!`, 
              { 
                variant: 'success',
                autoHideDuration: 4000,
              }
            )
          } catch (error) {
            enqueueSnackbar(
              `❌ Failed to award points. Please try again.`, 
              { 
                variant: 'error',
                autoHideDuration: 4000,
              }
            )
          }
        } else {
          enqueueSnackbar(
            `⚠️ Could not find user for ${assigneeName}`, 
            { 
              variant: 'warning',
              autoHideDuration: 4000,
            }
          )
        }
      } else {
      }
      
    } catch (error) {
    }
  }

  const handleDialogClose = () => {
    setOpenDialog(false)
  }

  const onEdit = (choreId) => {
    const choreToEdit = filteredChores.find((chore) => chore.id === choreId)
    if (choreToEdit) {
      if (
        choreToEdit.category === "daily" ||
        choreToEdit.category === "weekly" ||
        choreToEdit.category === "monthly"
      ) {
        setNewChore({
          id: choreToEdit.id,
          description: choreToEdit.description,
          assignee: choreToEdit.assignee,
          frequency: choreToEdit.frequency,
          category: choreToEdit.category,
        })
        setOpenDialog(true)
      } else {
        setNewTask({
          id: choreToEdit.id,
          description: choreToEdit.description,
          assignee: choreToEdit.assignee,
          category: choreToEdit.category,
        })
        setOpenTaskDialog(true)
      }
    }
  }

  const onDeleteRequested = (choreId) => {
    setChoreToDelete(choreId)
    setDeleteDialogOpen(true)
  }

  if (!assignee) {
    return <div />
  }

  const avatarSize = 200

  const fade =
    (!isMobile && assignee === "Connor") || (!isMobile && assignee === "Leah")
      ? 0.2
      : 0

  const handleMarkDone = (choreId, index) => {
    confettiRefs.current[index].triggerConfetti()

    // Call the original onMarkDone function
    onMarkDone(choreId)
  }

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      lg={2.4}
      justifyContent={"center"}
      key={assignee}
      sx={{ backgroundColor: `rgba(255,255,255,${fade})` }}
    >
      <Avatar
        src={avatars[assignee.toLowerCase()]}
        sx={{
          width: avatarSize,
          height: avatarSize,
          margin: "auto",
          marginTop: 2,
          marginBottom: 2,
        }}
        alt={assignee}
      />
      {/* {showSuccess && <Alert severity="success">Can Watch Screens!</Alert>} */}
      <Divider />
      <Stack spacing={0.25}>
        {filteredChores
          ?.sort((a, b) => a.description?.localeCompare(b.description))
          .map((chore, index) => (
            <ConfettiTrigger
              key={`${chore.id}-confetti`}
              ref={(el) => (confettiRefs.current[index] = el)}
            >
              <ChoresCard
                key={`${chore.id}-${index}`}
                id={chore.id}
                title={""}
                isComplete={isChoreComplete(chore)}
                description={chore.description}
                onMarkDone={() => handleMarkDone(chore.id, index)}
                onEdit={() => onEdit(chore.id)}
                onDelete={() => onDeleteRequested(chore.id)}
              />
            </ConfettiTrigger>
          ))}
      </Stack>
      {newChore.id !== null && (
        <AddEditChoreDialog
          open={openDialog}
          handleClose={handleDialogClose}
          chore={newChore}
          setChore={setNewChore}
        />
      )}
      {newTask.id !== null && (
        <AddOtherTasksDialog
          open={openTaskDialog}
          handleClose={() => setOpenTaskDialog(false)}
          chore={newTask}
          setChore={setNewTask}
          type={label}
        />
      )}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={onCloseDeleteDialog}
        choreId={choreToDelete}
      />
      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
          Chore deleted!
        </Alert>
      </Snackbar>
    </Grid>
  )
}

export default AssigneeChoresSection;